import { Component, HostBinding, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CrowdctrlFormatDatePipe } from '../../pipes/crowdctrl-format-date.pipe';
import { IonButton, IonCol, IonGrid, IonIcon, IonRow, IonSpinner } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { checkmarkCircle, printOutline, chevronForward } from 'ionicons/icons';
import { Order } from '@crowdctrl/core';
import { LoadingCardComponent } from '../loading-card/loading-card.component';
import { LoadingButtonComponent } from '../loading-button/loading-button.component';
import { OrderTicketPrintService } from '../../services/order-ticket-print.service';
import { CrowdctrlToastService } from '../../services/crowdctrl-toast.service';
import { extractErrorMessage } from '../../utils/error.utils';

addIcons({ checkmarkCircle, printOutline, chevronForward });

/** Parent completes navigation and reports success so dismiss loading can clear on failure. */
export interface OrderDetailDismissPayload {
  complete(success: boolean): void;
}

@Component({
  selector: 'crowdctrl-order-detail-view',
  standalone: true,
  imports: [
    CommonModule,
    CrowdctrlFormatDatePipe,
    IonButton,
    IonCol,
    IonGrid,
    IonIcon,
    IonRow,
    IonSpinner,
    LoadingCardComponent,
    LoadingButtonComponent,
  ],
  templateUrl: './order-detail-view.component.html',
  styleUrl: './order-detail-view.component.css',
})
export class OrderDetailViewComponent {
  @Input() order: Order | null = null;
  @Input() isLoading = false;
  @Input() errorMessage: string | null = null;
  @Input() showDismissButton = true;
  @Input() dismissButtonText = 'OK';
  /** When true, content flows for page-level scrolling instead of internal scroll */
  @Input() flowLayout = false;
  /** When true, only show main ticket number (session sequence), no meta or detail grid */
  @Input() compact = false;
  /**
   * When true, omit workspace/session/queue meta and the detail grid even if compact is false.
   * Use for temporary UX (e.g. Forms parity with agent); template block stays for easy re-enable.
   */
  @Input() hideExtendedOrderDetails = false;
  @Output() dismiss = new EventEmitter<OrderDetailDismissPayload>();

  /** True while waiting for routed parent to finish navigation after dismiss. */
  isDismissNavigating = false;

  @HostBinding('class.flow-layout-host') get flowLayoutHost(): boolean {
    return this.useFlowLayout;
  }

  @HostBinding('class.compact-layout') get compactLayoutHost(): boolean {
    return this.compact;
  }

  /**
   * Only use flow layout for the full success content.
   * Loading/error/empty states should stay vertically centered.
   * Compact success mode is also centered (used by the agent Order Placed page).
   */
  get useFlowLayout(): boolean {
    return (
      this.flowLayout && !this.compact && !this.isLoading && !this.errorMessage && !!this.order
    );
  }

  isPrinting = false;

  readonly orderTicketPrintService = inject(OrderTicketPrintService);
  private toastService = inject(CrowdctrlToastService);

  get customerIdentifier(): string | null {
    return this.order?.data?.['customerIdentifier'] || null;
  }

  get isNumericId(): boolean {
    const id = this.customerIdentifier;
    return id ? /^\d+$/.test(id) : false;
  }

  async onPrintClick(): Promise<void> {
    if (!this.order || this.isPrinting) return;
    this.isPrinting = true;
    try {
      await this.orderTicketPrintService.printOrderTicket(this.order);
    } catch (err) {
      const msg = extractErrorMessage(err);
      console.error('Print failed', err);
      await this.toastService.show({
        message: msg || 'Failed to print ticket.',
        duration: 5000,
        color: 'danger',
      });
    } finally {
      this.isPrinting = false;
    }
  }

  onDismissClick(): void {
    if (!this.showDismissButton || this.isDismissNavigating) {
      return;
    }
    this.isDismissNavigating = true;
    this.dismiss.emit({
      complete: (success: boolean) => {
        if (!success) {
          this.isDismissNavigating = false;
        }
      },
    });
  }

  /** Reset stuck loading (e.g. when the page is shown again after a failed navigation). */
  resetDismissNavigationState(): void {
    this.isDismissNavigating = false;
  }
}
